/**
 * Browser OpenTelemetry bootstrap.
 * Instruments fetch/XHR only for same-origin {@code /api/} and {@code /realtime-api/} URLs,
 * excluding high-volume routes via {@link shouldIgnoreUrlForBrowserTracing}.
 *
 * Configuration is normally supplied by {@link getOpenTelemetryConfig} from the backend
 * `GET /api/frontend/config` response. The optional `config` parameter allows the caller
 * to override the default/fallback values.
 */

import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import type { FrontendConfigResponseDto } from "../../types/dtos/api/frontend-config-response.dto";

import {
    buildFlowRideInstrumentedUrlMatchers,
    buildOpenTelemetryIgnoreUrlPatterns,
} from "./openTelemetryIgnoreUrls";

let initialized = false;

/**
 * Initializes OpenTelemetry instrumentation exactly once in the browser.
 *
 * @param config - Optional OpenTelemetry runtime configuration from the backend.
 *                 Falls back to safe defaults when absent.
 */
export const initializeOpenTelemetryBrowser = ( config?: FrontendConfigResponseDto["openTelemetry"] ): void => {
    if ( initialized || typeof window === "undefined" ) {
        return;
    }
    initialized = true;

    const exportEnabled = config?.browserExportEnabled ?? false;
    const serviceName = config?.serviceName?.trim() || "flowride-frontend";
    const ratio = typeof config?.tracesSamplerRatio === "number" && Number.isFinite( config.tracesSamplerRatio )
        ? Math.min( 1, Math.max( 0, config.tracesSamplerRatio ) )
        : 1;

    const tracesEndpoint = config?.exporterOtlpTracesEndpoint?.trim();
    const defaultTracesUrl = `${ window.location.origin }/api/observability/otlp/traces`;

    const spanProcessors = [];
    if ( exportEnabled ) {
        const exporter = new OTLPTraceExporter( {
            "url":           tracesEndpoint !== undefined && tracesEndpoint !== "" ? tracesEndpoint : defaultTracesUrl,
            "timeoutMillis": 15_000,
        } );
        spanProcessors.push( new BatchSpanProcessor( exporter, {
            "maxExportBatchSize":   128,
            "scheduledDelayMillis": 5_000,
        } ) );
    }

    const provider = new WebTracerProvider( {
        "resource":       resourceFromAttributes( { "service.name": serviceName } ),
        "sampler":        new ParentBasedSampler( { "root": new TraceIdRatioBasedSampler( ratio ) } ),
        "spanProcessors": spanProcessors.length > 0 ? spanProcessors : undefined,
    } );
    provider.register( {
        "contextManager": new ZoneContextManager(),
        "propagator":     new W3CTraceContextPropagator(),
    } );

    const urlMatchers = buildFlowRideInstrumentedUrlMatchers();
    const ignoreUrls = buildOpenTelemetryIgnoreUrlPatterns();
    registerInstrumentations( {
        "instrumentations": [
            new FetchInstrumentation( {
                "propagateTraceHeaderCorsUrls": urlMatchers,
                "clearTimingResources":         true,
                ignoreUrls,
            } ),
            new XMLHttpRequestInstrumentation( {
                "propagateTraceHeaderCorsUrls": urlMatchers,
                ignoreUrls,
            } ),
        ],
    } );
};
